import { API_BASE_URL } from '../config';

/** Canonical localStorage key for the JWT (must match Login / App). */
export const AUTH_TOKEN_KEY = 'token';
export const AUTH_USER_KEY = 'user';
/** Whether the current session was created with "Keep me signed in". */
export const AUTH_KEEP_SIGNED_IN_KEY = 'keepSignedIn';

/** Refresh when less than this much time remains (safety net near expiry). */
const REFRESH_SKEW_MS = 10 * 60 * 1000;

let refreshInFlight: Promise<boolean> | null = null;
let authWatchStarted = false;

export type JwtPayload = {
  email?: string;
  role?: string;
  persist?: boolean;
  exp?: number;
  iat?: number;
};

export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function getKeepSignedIn(): boolean {
  return localStorage.getItem(AUTH_KEEP_SIGNED_IN_KEY) === '1';
}

export function setKeepSignedIn(value: boolean): void {
  if (value) {
    localStorage.setItem(AUTH_KEEP_SIGNED_IN_KEY, '1');
  } else {
    localStorage.removeItem(AUTH_KEEP_SIGNED_IN_KEY);
  }
}

export function clearAuth(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_KEEP_SIGNED_IN_KEY);
}

export function redirectToLogin(): void {
  clearAuth();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

/** Decode JWT payload without verifying (server still validates). */
export function parseJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as JwtPayload) : null;
  } catch {
    return null;
  }
}

export function getTokenExpiryMs(token: string | null = getAuthToken()): number | null {
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return null;
  return payload.exp * 1000;
}

/** True when the access token is past exp (client clock). */
export function isTokenExpired(token: string | null = getAuthToken(), now = Date.now()): boolean {
  const exp = getTokenExpiryMs(token);
  if (exp == null) return !token;
  return exp <= now;
}

/**
 * Refresh when past the halfway point of the token lifetime, or within the
 * skew window — so active use slides short (1h) and persist (30d) sessions.
 */
export function shouldRefreshToken(token: string | null = getAuthToken(), now = Date.now()): boolean {
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return false;
  const expMs = payload.exp * 1000;
  const remaining = expMs - now;
  if (remaining <= 0) return false;
  if (remaining <= REFRESH_SKEW_MS) return true;
  if (typeof payload.iat === 'number') {
    const lifetime = (payload.exp - payload.iat) * 1000;
    if (lifetime > 0 && remaining < lifetime / 2) return true;
  }
  return false;
}

async function doRefreshAuthToken(): Promise<boolean> {
  const token = getAuthToken();
  if (!token || isTokenExpired(token)) return false;
  try {
    const res = await fetch(resolveApiUrl('/api/auth/refresh'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) return false;
    const data = (await res.json()) as {
      token?: string;
      keepSignedIn?: boolean;
      user?: { email?: string; role?: string };
    };
    if (!data.token || typeof data.token !== 'string') return false;
    setAuthToken(data.token);
    if (typeof data.keepSignedIn === 'boolean') {
      setKeepSignedIn(data.keepSignedIn);
    } else {
      const payload = parseJwtPayload(data.token);
      setKeepSignedIn(!!payload?.persist);
    }
    if (data.user && data.user.email) {
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
    }
    return true;
  } catch {
    return false;
  }
}

/** Sliding renewal; coalesces concurrent callers. */
export async function refreshAuthToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = doRefreshAuthToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** Proactively renew when halfway through the TTL / near expiry. */
export async function ensureFreshToken(): Promise<void> {
  const token = getAuthToken();
  if (!token || isTokenExpired(token)) return;
  if (!shouldRefreshToken(token)) return;
  await refreshAuthToken();
}

/**
 * Resume / visibility: try to slide the session so phone sleep + return
 * does not strand an almost-expired token mid-task.
 */
export function startAuthSessionWatch(): void {
  if (authWatchStarted || typeof window === 'undefined') return;
  authWatchStarted = true;

  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void ensureFreshToken();
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);
  // Kick once after load (e.g. restoring a PWA).
  void ensureFreshToken();
}

/** Test helper: reset in-flight refresh + watch flag. */
export function _resetAuthSessionStateForTests(): void {
  refreshInFlight = null;
  authWatchStarted = false;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    if (body && typeof body === 'object' && body !== null && 'code' in body) {
      this.code = String((body as { code?: unknown }).code || '');
    }
  }
}

/**
 * Parse a failed Response into a human-readable message.
 * Prefers JSON `{ error }` / `{ message }`, falls back to text.
 */
export async function readApiErrorMessage(response: Response): Promise<string> {
  const status = response.status;
  try {
    const data = await response.clone().json();
    if (data && typeof data === 'object') {
      const err = (data as { error?: unknown; message?: unknown }).error
        ?? (data as { message?: unknown }).message;
      if (typeof err === 'string' && err.trim()) {
        return err;
      }
      return `HTTP ${status}: ${JSON.stringify(data).slice(0, 300)}`;
    }
  } catch {
    /* not JSON */
  }
  try {
    const text = (await response.clone().text()).trim();
    if (text) return text.slice(0, 300);
  } catch {
    /* ignore */
  }
  return `HTTP ${status}`;
}

export type ApiFetchOptions = RequestInit & {
  /** Skip Bearer attachment and 401 redirect (use for /api/login only). */
  skipAuth?: boolean;
  /**
   * When true, do not throw on non-2xx (caller will check res.ok).
   * Default false: apiFetch throws ApiError with the server JSON error message.
   */
  allowHttpError?: boolean;
};

/**
 * Resolve a request URL against API_BASE_URL when given an /api path.
 */
export function resolveApiUrl(input: string): string {
  if (/^https?:\/\//i.test(input)) {
    return input;
  }
  if (input.startsWith('/api')) {
    return `${API_BASE_URL}${input}`;
  }
  if (input.startsWith('api/')) {
    return `${API_BASE_URL}/${input}`;
  }
  return input;
}

/** Delay before retrying a GET that failed at the network level. */
const NETWORK_RETRY_DELAY_MS = 600;

function isRetryableNetworkError(err: unknown, signal?: AbortSignal | null): boolean {
  if (signal?.aborted) return false;
  // fetch() rejects with TypeError ("Failed to fetch" / "Load failed") when the
  // request never got a response: a phone waking up on a dead connection, a
  // wifi/cellular hop, etc. AbortError is a DOMException, not a TypeError.
  return err instanceof TypeError;
}

/**
 * fetch() with one quiet retry for GET/HEAD when the request failed before any
 * response came back. A single network blip on a phone otherwise leaves a page
 * showing "Failed to fetch" until the user refreshes. Writes are never retried.
 */
export async function fetchWithNetworkRetry(url: string, init: RequestInit = {}): Promise<Response> {
  const method = String(init.method || 'GET').toUpperCase();
  const canRetry = method === 'GET' || method === 'HEAD';
  try {
    return await fetch(url, init);
  } catch (err) {
    if (!canRetry || !isRetryableNetworkError(err, init.signal)) throw err;
    await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS));
    if (init.signal?.aborted) throw err;
    return fetch(url, init);
  }
}

async function authorizedFetch(
  url: string,
  rest: RequestInit,
  headers: Headers
): Promise<Response> {
  await ensureFreshToken();
  const token = getAuthToken();
  if (!token) {
    redirectToLogin();
    throw new ApiError('No authentication token', 401);
  }
  if (isTokenExpired(token)) {
    redirectToLogin();
    throw new ApiError('Session expired, please log in again', 401);
  }
  headers.set('Authorization', `Bearer ${token}`);
  return fetchWithNetworkRetry(url, { ...rest, headers });
}

/**
 * Authenticated fetch for Tilly API routes.
 * Attaches Bearer token from localStorage, slides the session when near
 * expiry, retries once after refresh on 401, then redirects to /login
 * (403 = signed in but not allowed; it must not log the user out),
 * and throws ApiError with the server message on other non-2xx responses
 * (unless allowHttpError or skipAuth is set).
 */
export async function apiFetch(input: string, options: ApiFetchOptions = {}): Promise<Response> {
  const { skipAuth = false, allowHttpError = false, headers: initHeaders, ...rest } = options;
  const url = resolveApiUrl(input);

  const headers = new Headers(initHeaders || undefined);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  let response: Response;
  if (skipAuth) {
    response = await fetchWithNetworkRetry(url, { ...rest, headers });
  } else {
    response = await authorizedFetch(url, rest, headers);

    // Race: token expired between ensureFresh and server verify — try one refresh+retry.
    if (response.status === 401) {
      const renewed = await refreshAuthToken();
      if (renewed) {
        const retryHeaders = new Headers(initHeaders || undefined);
        if (!retryHeaders.has('Accept')) {
          retryHeaders.set('Accept', 'application/json');
        }
        const token = getAuthToken();
        if (token) {
          retryHeaders.set('Authorization', `Bearer ${token}`);
          response = await fetchWithNetworkRetry(url, { ...rest, headers: retryHeaders });
        }
      }
    }

    if (response.status === 401) {
      redirectToLogin();
      throw new ApiError('Session expired, please log in again', response.status);
    }
  }

  // Login and other skipAuth callers handle status themselves.
  if (!skipAuth && !allowHttpError && !response.ok) {
    let body: unknown;
    try {
      body = await response.clone().json();
    } catch {
      body = undefined;
    }
    const message = await readApiErrorMessage(response);
    throw new ApiError(message, response.status, body);
  }

  return response;
}
